import { useAuth } from '../context/AuthContext';
import { Linkedin, Search, Globe, Github, Instagram, Briefcase, ArrowUpRight, Clock } from 'lucide-react';

// Short by design. The long version lives in the collapsible explainer underneath.
const PLATFORMS = [
  { icon: Linkedin, name: 'LinkedIn', tag: 'Start here', hue: '#38bdf8',
    line: 'Where recruiters search and alumni check you.', href: 'https://www.linkedin.com/' },
  { icon: Briefcase, name: 'Handshake', tag: 'Students', hue: '#34d399',
    line: 'How colleges and employers recruit you directly.', href: 'https://joinhandshake.com/students/' },
  { icon: Github, name: 'GitHub · Behance', tag: 'Show the work', hue: '#a78bfa',
    line: 'One finished project beats a page of adjectives.', href: 'https://github.com/' },
  { icon: Globe, name: 'Your own page', tag: 'Own the link', hue: '#fbbf24',
    line: 'One free page you control. Put it everywhere.', href: 'https://sites.google.com/new' },
  { icon: Instagram, name: 'Your social', tag: 'Clean it up', hue: '#f472b6',
    line: 'They look. Old posts cost real offers.', href: null },
];

export default function BrandPrimer() {
  const { user } = useAuth();
  const name = user?.full_name || '';
  const googleMe = `https://www.google.com/search?q=${encodeURIComponent(`"${name}"`)}`;

  return (
    <div className="mb-6" data-testid="brand-primer">
      <div className="relative overflow-hidden rounded-2xl border border-sky-300/25 bg-slate-900/50 p-5">
        <span aria-hidden className="absolute -top-16 -left-10 h-40 w-40 rounded-full bg-sky-400/20 blur-3xl" />
        <span aria-hidden className="absolute -bottom-20 right-0 h-40 w-40 rounded-full bg-violet-500/20 blur-3xl" />

        <div className="relative flex flex-wrap items-center gap-x-3 gap-y-1">
          <Linkedin className="w-4 h-4 text-sky-300" />
          <h3 className="text-base md:text-lg font-semibold text-white">
            This is your LinkedIn, and everywhere else people look you up.
          </h3>
          <span className="inline-flex items-center gap-1 text-[11px] text-amber-200/90">
            <Clock className="w-3 h-3" /> They decide in 6 seconds
          </span>
        </div>
        <p className="relative text-sm text-slate-300/85 mt-1.5">
          The two scores above are how you look to a stranger. Raise them here.
        </p>

        <div className="relative mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
          {PLATFORMS.map(p => {
            const inner = (
              <>
                <span aria-hidden
                  className="absolute inset-x-0 top-0 h-[2px] opacity-50 group-hover:opacity-100 transition-opacity duration-200"
                  style={{ background: `linear-gradient(90deg, transparent, ${p.hue}, transparent)` }} />
                <div className="flex items-center gap-2">
                  <p.icon className="w-4 h-4 shrink-0" style={{ color: p.hue }} />
                  <p className="text-sm font-semibold text-white truncate">{p.name}</p>
                  {p.href && (
                    <ArrowUpRight className="w-3.5 h-3.5 ml-auto shrink-0 opacity-40 group-hover:opacity-100 group-hover:-translate-y-0.5 transition-all duration-200"
                      style={{ color: p.hue }} />
                  )}
                </div>
                <p className="text-[11px] mt-1" style={{ color: p.hue }}>{p.tag}</p>
                <p className="text-xs text-slate-300/80 mt-1">{p.line}</p>
              </>
            );
            const cls = 'group relative block rounded-xl border p-3 overflow-hidden transition-all duration-200 hover:-translate-y-0.5';
            const style = {
              borderColor: `${p.hue}33`,
              background: `linear-gradient(150deg, ${p.hue}12, rgba(255,255,255,0.02))`,
              boxShadow: `0 0 0 0 ${p.hue}`,
            };
            const testid = `brand-platform-${p.name.split(' ')[0].toLowerCase()}`;
            return p.href
              ? <a key={p.name} href={p.href} target="_blank" rel="noreferrer"
                  className={cls} style={style} data-testid={testid}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 10px 30px -12px ${p.hue}99`; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = `0 0 0 0 ${p.hue}`; }}>{inner}</a>
              : <div key={p.name} className={cls} style={style} data-testid={testid}>{inner}</div>;
          })}

          {name && (
            <a href={googleMe} target="_blank" rel="noreferrer" data-testid="brand-platform-google"
              className="group relative block rounded-xl border border-cyan-300/30 p-3 overflow-hidden transition-all duration-200 hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(150deg, #22d3ee14, rgba(255,255,255,0.02))' }}>
              <span aria-hidden className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-cyan-300 to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-200" />
              <div className="flex items-center gap-2">
                <Search className="w-4 h-4 text-cyan-300 shrink-0" />
                <p className="text-sm font-semibold text-white truncate">Search yourself</p>
                <ArrowUpRight className="w-3.5 h-3.5 ml-auto text-cyan-300 opacity-40 group-hover:opacity-100 group-hover:-translate-y-0.5 transition-all duration-200" />
              </div>
              <p className="text-[11px] text-cyan-300 mt-1">Do this first</p>
              <p className="text-xs text-slate-300/80 mt-1">See what they see when they type your name.</p>
            </a>
          )}
        </div>

        <p className="relative text-[11px] text-slate-300 mt-3.5">
          We never post, message anyone or sign in for you.
        </p>
      </div>
    </div>
  );
}
